import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Building2, Phone, Mail, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, PageHeader } from "../components/common";
import { DISTRICTS, TOWNS } from "../mock";
import { useSiteData } from "../context/SiteData";

function districtOf(outlet) {
  return DISTRICTS.find((d) => d.toLowerCase().replace(/\s+/g, "") === outlet.group) || outlet.town;
}

export default function Locations() {
  const { data, loading, loaded } = useSiteData();
  const [filter, setFilter] = useState("All");

  const outlets = loaded ? data.outlets : [];
  const headOffice = outlets.find((o) => o.id === "ambala-cantt-warehouse");
  const filters = ["All", ...DISTRICTS];
  const visible = filter === "All" ? outlets : outlets.filter((o) => districtOf(o) === filter);

  return (
    <>
      <PageHeader
        crumb="Locations"
        title="Thirteen warehouses & outlets, seven districts"
        intro="Filter by district to find the nearest Rajiv Syndicate warehouse or retail outlet across Northern Haryana."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal><Eyebrow>Outlet locator</Eyebrow></Reveal>
        <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Our network across Northern Haryana</h2></Reveal>
        <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5 max-w-2xl">Seven districts run from the Ambala Cantt head office. Filter the directory below to find outlets in your district.</p></Reveal>

        {/* Filter pills */}
        <Reveal delay={180}>
          <div className="flex flex-wrap gap-2.5 mt-10">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-sm font-medium rounded-lg px-4 py-2.5 border transition-all ${
                  filter === f
                    ? "bg-[var(--rs-blue)] text-white border-[var(--rs-blue)] shadow-md"
                    : "bg-white text-[var(--rs-ink)] border-[var(--rs-border)] hover:border-[var(--rs-blue)] hover:text-[var(--rs-blue)]"
                }`}
              >
                {f === "All" ? "All Districts" : f}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 mt-12">
          {/* Stylised map panel */}
          <Reveal>
            <div className="relative rounded-2xl border border-[var(--rs-border)] bg-gradient-to-br from-[#eef3fe] to-[#f7f9fd] p-8 h-full min-h-[360px] overflow-hidden">
              <span className="font-mono-label text-xs text-[var(--rs-muted)] uppercase tracking-wider">Northern Haryana · 7 districts</span>
              <div className="grid grid-cols-2 gap-4 mt-6">
                {DISTRICTS.map((d, i) => (
                  <button
                    key={d}
                    onClick={() => setFilter(d)}
                    className={`flex items-center gap-2.5 text-left rounded-xl px-4 py-3.5 border transition-all ${
                      filter === d ? "bg-white border-[var(--rs-blue)] shadow-md" : "bg-white/60 border-transparent hover:bg-white hover:border-[var(--rs-border)]"
                    }`}
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <MapPin size={17} className={filter === d ? "text-[var(--rs-blue)]" : "text-[var(--rs-muted)]"} />
                    <span className="text-sm font-medium">{d}</span>
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Directory */}
          <div>
            <Reveal>
              <div className="bg-white border border-[var(--rs-border)] rounded-2xl p-7 mb-6">
                <span className="font-mono-label text-xs text-[var(--rs-blue)] uppercase tracking-wider">Head office</span>
                <h3 className="font-head font-bold text-xl mt-2">{headOffice ? headOffice.address + ", " + headOffice.town : "Kuldeep Nagar, GT Road, Ambala Cantt"}</h3>
                {headOffice && headOffice.mapsUrl ? (
                  <a href={headOffice.mapsUrl} target="_blank" rel="noopener noreferrer" className="btn-primary rounded-lg px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 mt-5">Get Directions <ArrowRight size={15} /></a>
                ) : (
                  <Link to="/contact" className="btn-primary rounded-lg px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 mt-5">Get Directions & Contact Details <ArrowRight size={15} /></Link>
                )}
              </div>
            </Reveal>

            {!loaded && !loading && (
              <div className="grid sm:grid-cols-2 gap-4">
                {(filter === "All" ? TOWNS : TOWNS).map((t, i) => (
                  <Reveal key={t} delay={i * 50}>
                    <div className="card-hover bg-white border border-[var(--rs-border)] rounded-xl p-5 flex items-start gap-3 h-full">
                      <div className="w-10 h-10 rounded-lg bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center shrink-0"><Building2 size={18} /></div>
                      <div>
                        <h4 className="font-head font-semibold text-base">{t}</h4>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}

            {loading && (
              <p className="text-[var(--rs-muted)] py-6">Loading outlets…</p>
            )}

            {loaded && (
              <div className="grid sm:grid-cols-2 gap-4">
                {visible.map((o, i) => (
                  <Reveal key={o.id} delay={i * 50}>
                    <Link
                      to={`/locations/${o.id}`}
                      className="card-hover bg-white border border-[var(--rs-border)] rounded-xl p-5 flex flex-col gap-3 h-full hover:border-[var(--rs-blue)] transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center shrink-0"><Building2 size={18} /></div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-head font-semibold text-base">{o.name}</h4>
                            {o.code && <span className="font-mono-label text-[10px] text-[var(--rs-blue)] bg-[#eef3fe] rounded px-1.5 py-0.5">{o.code}</span>}
                          </div>
                          <p className="text-[var(--rs-muted)] text-xs mt-0.5">{o.tag} · {districtOf(o)} district</p>
                        </div>
                      </div>
                      <p className="text-sm text-[var(--rs-muted)] flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0" />{o.address}</p>
                      {o.phone && <p className="text-sm text-[var(--rs-muted)] flex items-center gap-2"><Phone size={14} className="shrink-0" />{o.phone}</p>}
                      {o.email && <p className="text-sm text-[var(--rs-muted)] flex items-center gap-2 truncate"><Mail size={14} className="shrink-0" />{o.email}</p>}
                    </Link>
                  </Reveal>
                ))}
                {visible.length === 0 && (
                  <p className="text-[var(--rs-muted)] col-span-2 py-6">No outlets listed for this district yet.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
